import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom'
import {
  LayoutDashboard,
  Star,
  Layers,
  FolderTree,
  Users,
  FileBarChart,
  BarChart3,
  Medal,
  ChevronDown,
  ChevronRight,
  Plus,
  Eye,
  EyeOff,
  Archive,
  ArchiveRestore,
  ClipboardList,
  ListChecks,
  CalendarDays,
  CalendarRange,
  UserCog,
  Workflow,
  Package,
  Menu,
  Target,
  BookOpen,
  FolderOpen,
  Settings as SettingsIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core'
import { Logo } from '../Logo'
import { ErrorBoundary } from '../ErrorBoundary'
import { BrandMarkIcon } from '../BrandMarkIcon'
import { UserMenu } from '../UserMenu'
import { OrganisationSwitcher } from '../OrganisationSwitcher'
import { NotificationsBell } from '../NotificationsBell'
import { CollapsedTooltipWrap } from '../CollapsedNavTooltip'
import { FontSizeMenu } from '../FontSizeMenu'
import { ThemeToggle } from '../ui/ThemeToggle'
import { DateTimeClock } from '../DateTimeClock'
import { QuickCreateButton } from '../QuickCreateModal'
// Quick-create (+) button is hidden by request. Flip this flag to
// true to re-enable it in the user topbar (one-line revert).
const SHOW_QUICK_CREATE = false
// EndOfDayButton import removed 2026-05-27 — the End-of-Day dialog
// was time-tracking-dependent (stopped your active timer + listed
// today's time entries) and gated behind `{false ? ... : null}`
// anyway. The dialog file was deleted with the rest of the Time
// Tracking module.
import { SpaceCreateDialog } from '../SpaceCreateDialog'
import { SpaceRowActions } from '../SpaceRowActions'
import { SpaceChildren } from '../SpaceChildren'
import { SpaceShareDialog } from '../SpaceShareDialog'
import { SpacesModal } from '../SpacesModal'
import { ProductTour } from '../ProductTour/ProductTour'
import { useTourStore } from '../ProductTour/useProductTour'
import { AdminSpacesTree } from './AdminSpacesTree'
import { AdminCurrentSpaceTree } from './AdminCurrentSpaceTree'
import { usePersonalSpaces } from '../../lib/personal-spaces'
import { SidebarToggle } from './SidebarToggle'
import { useAuthStore } from '../../stores/authStore'
import { useSidebarCollapsed, useSidebarStore } from '../../stores/sidebarStore'
import {
  useFavoriteLists,
  useFavoriteListMeta,
  useFavoriteSpaces,
  useHiddenSpaces,
  useExpandedSpace,
} from '../../lib/space-prefs'
import { api, extractApiError } from '../../lib/api'
import {
  spaceListResponse,
  workspaceListResponse,
} from '../../lib/types'
import { dropSpaceId } from '../../lib/sidebar-dnd'
import { userHasOperation } from '../../lib/permissions'

// ----------------------------------------------------------------------
// Shared sidebar styling — kept in sync with AdminLayout + SuperAdmin
// so all three panels read as the same component family. Active rows
// get a translucent white pill + inset highlight ring; inactive rows
// only lighten on hover (no horizontal translate).
//
// The slide-right `hover:translate-x-0.5` was removed: it only fired
// on the flat NavLink rows (Dashboard, SOPs Library, Files & Links,
// Performance) and not on the SidebarSection rows (Favorites, Spaces,
// Manager, Personal, Reports), which made the sidebar feel
// inconsistent — half the items "jumped" on hover, the other half
// stayed put. Now every row has the same calm bg-only hover.
// ----------------------------------------------------------------------
// ROW_BASE used to include `px-3 py-2.5` but the sidebar's collapsed
// mode needs to swap to `px-0 py-2.5 justify-center` to centre the
// icon inside the 68px column. Padding now lives at the call site so
// each NavLink can flip on `collapsed`; ROW_BASE keeps the rest of
// the shared shape (gap / rounding / cursor / transition).
const ROW_BASE =
  'group flex items-center gap-3 rounded-[10px] transition-colors cursor-pointer'
const ROW_ACTIVE =
  'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
const ROW_INACTIVE =
  'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white'

/** Collapsible section header with the same flat-row look as the
 *  top-level NavLinks. Chevron sits at the right; hover-revealed
 *  action buttons replace the chevron position when present.
 *
 *  Open state is owned by the parent so the sidebar behaves like an
 *  accordion — only one section can be open at a time. The parent
 *  passes `openSection` (the id of the currently open section) and
 *  `onToggle(id, force?)` (toggles that id; `force === true` is used
 *  by the auto-open effect when the active route lives inside the
 *  section). */
function SidebarSection({
  id,
  title,
  icon: Icon,
  matchPaths,
  excludePaths,
  headerActive = true,
  openSection,
  onToggle,
  actions,
  children,
  dataTour,
}) {
  const location = useLocation()
  // Nested sections (Favorites / Spaces / Manager / Personal / Reports)
  // don't fit a 68px icon-only rail — the chevron, action chips, and
  // sub-tree all need room. Self-hide here so every call site can
  // stay as-is and the layout's collapsed mode shows only the flat
  // top-level NavLinks. Reading the store directly (rather than via a
  // prop) means the call sites don't have to plumb the flag through.
  // While the product tour runs, treat the rail as expanded so the section
  // headers are mounted for the tour to spotlight. Sections stay CLOSED and open
  // one at a time as the tour reaches them (`tourActiveGroup` = the section the
  // current step lives in, matched by this section's `id`).
  const tourActive = useTourStore((s) => s.phase !== 'idle')
  const tourActiveGroup = useTourStore((s) => s.activeGroup)
  const collapsed = useSidebarCollapsed() && !tourActive
  const matchesPath = (paths) =>
    paths?.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
    ) ?? false
  // A section OWNS the current route when it matches `matchPaths` AND is not
  // excluded. `excludePaths` lets the Spaces section cede to Favourites when the
  // open Space/List is favourited.
  const routeOwned = matchesPath(matchPaths) && !matchesPath(excludePaths)
  // Whether to PAINT the section header as active (pill + left bar + bold).
  // `headerActive={false}` opts out: the section still auto-opens on its route,
  // but the active state stays on the child row (the favourite item), not the
  // section header — the Favourites section shouldn't look "active" itself.
  const hasActiveChild = routeOwned && headerActive

  // Auto-open whichever section owns the current route. The dependency is
  // `routeOwned` (not the styling flag) so a section that opts out of the
  // active header still auto-opens. Firing only on the transition means
  // navigating between two siblings inside the section doesn't override a
  // manual close.
  useEffect(() => {
    if (routeOwned) onToggle(id, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOwned])

  // Bail AFTER all hook calls — moving the early return above the
  // useEffect would violate the rules of hooks when the user toggles
  // the sidebar mid-mount (different number of hook calls per render).
  //
  // Collapsed mode used to `return null` here (hiding the whole
  // section), which made the 68px rail asymmetric: only flat
  // NavLink rows (Dashboard / SOPs Library / Files & Links /
  // Leaderboard) survived, while sectioned items (Favourites,
  // Spaces, Manager, Reports) disappeared. The user couldn't
  // navigate to those areas at all without expanding the sidebar
  // first. Now we render a single icon row in their place —
  // clicking it expands the sidebar AND opens this section, so
  // the user lands exactly where they would have via the chevron
  // path in the expanded view. Active-state highlight follows
  // `hasActiveChild` so the rail still shows the user which
  // section owns the current route.
  if (collapsed) {
    return (
      <CollapsedTooltipWrap label={title} collapsed>
        <button
          type="button"
          aria-label={title}
          onClick={() => {
            // Expand the sidebar FIRST so the section's chevron-
            // expand animation has somewhere to render, then open
            // this section in the now-expanded panel.
            useSidebarStore.getState().set(false)
            onToggle(id, true)
          }}
          className={
            `${ROW_BASE} px-0 py-2.5 justify-center w-full ` +
            (hasActiveChild ? ROW_ACTIVE : ROW_INACTIVE)
          }
        >
          <Icon size={18} />
        </button>
      </CollapsedTooltipWrap>
    )
  }

  // During the tour, open ONLY the section the current step lives in (matched by
  // id); every other section stays closed. Outside the tour it's the normal
  // accordion driven by `openSection`.
  const open = tourActive ? tourActiveGroup === id : openSection === id
  const hasActions = actions && actions.length > 0

  return (
    <div data-tour={dataTour}>
      <div
        className={
          'group/sec relative rounded-[10px] transition-all ' +
          (hasActiveChild
            ? 'bg-[color:var(--color-accent-bg-strong)] shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
            : 'hover:bg-[color:var(--color-card-hover)]')
        }
      >
        <button
          type="button"
          onClick={() => onToggle(id)}
          aria-expanded={open}
          aria-current={hasActiveChild ? 'page' : undefined}
          className={
            'w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer ' +
            (hasActiveChild
              ? 'text-white font-semibold'
              : 'text-[color:var(--color-ink-strong)]')
          }
        >
          <Icon
            size={18}
            className="shrink-0"
            strokeWidth={hasActiveChild ? 2.25 : 1.75}
          />
          <span className="flex-1 truncate">{title}</span>
          <ChevronDown
            size={14}
            className={
              'shrink-0 transition-all duration-150 ' +
              (open ? '' : '-rotate-90') +
              (hasActions ? ' group-hover/sec:opacity-0' : '')
            }
          />
        </button>
        {hasActions ? (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-100 lg:opacity-0 lg:group-hover/sec:opacity-100 transition-opacity">
            {actions.map((a) => {
              const ActionIcon = a.icon
              return (
                <button
                  key={a.label}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    a.onClick()
                  }}
                  title={a.label}
                  aria-label={a.label}
                  className={
                    'p-1.5 rounded cursor-pointer transition-colors ' +
                    (hasActiveChild
                      ? 'text-[color:var(--color-accent)] hover:bg-[color:var(--color-accent-bg)]/70'
                      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]')
                  }
                >
                  <ActionIcon size={13} strokeWidth={2} />
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
      {open ? <div className="mt-1.5 space-y-1 pl-3">{children}</div> : null}
    </div>
  )
}

// ----------------------------------------------------------------------
// Section-child link — used inside SidebarSection for static sub-pages.
// ----------------------------------------------------------------------
// Sub-link inside an expanded SidebarSection. Uses a plain Link (not
// NavLink) so the router doesn't stamp `aria-current="page"` — the
// global `aside [aria-current='page']::before` rule would otherwise
// add the green capsule to the sub-link, but the capsule should only
// belong to the parent section header. Active state here is just bold
// accent text.
function SidebarChildLink({ to, label, icon: Icon, end }) {
  const location = useLocation()
  const isActive = end
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + '/')
  return (
    <Link
      to={to}
      className={
        // Font size bumped from 13px → text-sm (14px) so nested
        // dropdown items (Spaces > Web Platform > List, Manager
        // section links, etc.) are easier to scan. Matches the
        // Company Admin sidebar's nested-link size so both panels
        // read at the same density. Active state gets the same
        // pill-on-gradient treatment as top-level rows.
        // Slide-right hover removed for consistency with the
        // top-level rows — see the note on ROW_INACTIVE above.
        'group flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm transition-colors cursor-pointer ' +
        (isActive
          ? 'bg-[color:var(--color-accent-bg-strong)] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
          : 'text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-white')
      }
    >
      <Icon
        size={17}
        className={
          isActive
            ? 'text-white shrink-0'
            : 'shrink-0 group-hover:text-white transition-colors'
        }
        strokeWidth={isActive ? 2.25 : 1.75}
      />
      <span className="truncate">{label}</span>
    </Link>
  )
}

// ----------------------------------------------------------------------
// Static top-level nav items. Split into two groups around the Messages
// inbox: daily-action items above (Dashboard, Focus Board), reference /
// gamification items below (SOPs, Achievements). Messages itself is
// rendered between them in the JSX so the unread-count badge sits at
// the natural daily-flow position — right after "where do I focus
// today?" and right before "anything to reference?".
//
// For Managers this matters especially — they receive team requests
// and replies as Messages, so the inbox is closer to a primary action
// than a secondary one. Other roles benefit too.
// ----------------------------------------------------------------------
const TOP_NAV_DAILY = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  // Focus Board removed from the sidebar — the header now carries
  // a dedicated pill button so it stays one click away without the
  // duplication. Route still resolves.
  // Notifications has NO sidebar row — it's reached from the header bell's
  // "View all notifications" link. (Removed by request across all panels.)
]
const TOP_NAV_REFERENCE = [
  { to: '/app/sops-library', label: 'SOPs Library', icon: BookOpen, end: false, requires: 'sop_integration' },
  // Files & Links is visible to everyone. Externals get a grant-scoped view
  // (only files/links/folders in their granted Spaces); the page hides its
  // mutation controls for them — see FilesPage.jsx.
  { to: '/app/files-links', label: 'Files & Links', icon: FolderOpen, end: false },
  // Performance link hidden from the sidebar across all panels per
  // user request. The route `/app/performance` is still mounted in
  // App.jsx so any deep links / bookmarks resolve; it just doesn't
  // appear in the nav rail. Re-add this entry to surface it again.
]
// Bottom cluster — arranged per product request so the rail ENDS with:
//   Goals → Reports (section) → Leaderboard → Our Products → User Manual.
// Goals renders directly ABOVE the personal "Reports" section; Leaderboard +
// Our Products directly BELOW it. User Manual stays the final row (mounted
// after these). Same feature/external gate as the groups above.
const BOTTOM_NAV_ABOVE_REPORTS = [
  // Goals — one page with tabs: Weekly Goals, Weekly Analysis, Department
  // Goals. Plan-gated (fail-open) via `weekly_goals`; internal members only.
  {
    to: '/app/goals',
    label: 'Goals',
    icon: Target,
    end: false,
    requires: 'weekly_goals',
    internalOnly: true,
  },
]
const BOTTOM_NAV_BELOW_REPORTS = [
  // Leaderboard — gamification standings; feature-gated via `gamification`.
  { to: '/app/leaderboard', label: 'Leaderboard', icon: Medal, end: false, requires: 'gamification' },
  // Our Products — the cross-product catalogue; visible to every user.
  { to: '/app/our-products', label: 'Our Products', icon: Package, end: false },
]

export function UserLayout() {
  const { user, signOut } = useAuthStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Force the rail expanded while the product tour runs so every menu + submenu
  // is mounted with labels for the tour to spotlight (reverts when it ends).
  const tourActive = useTourStore((s) => s.phase !== 'idle')
  const collapsed = useSidebarCollapsed() && !tourActive
  // Mobile off-canvas drawer state (shared store).
  const mobileOpen = useSidebarStore((s) => s.mobileOpen)
  const openMobile = useSidebarStore((s) => s.openMobile)
  const closeMobile = useSidebarStore((s) => s.closeMobile)
  // The page content scrolls inside <main> (below), not the window, so a
  // route change keeps the previous scroll offset — e.g. clicking a
  // bottom-of-dashboard quick action lands the next page scrolled to its
  // bottom. Reset <main> to the top whenever the path changes; also close
  // the mobile drawer so navigation dismisses it.
  const mainRef = useRef(null)
  const { pathname } = useLocation()
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 })
    closeMobile()
  }, [pathname, closeMobile])
  // User-shell layout keeps the inline Spaces tree below — the
  // SpacesModal (used in AdminLayout) was reverted here per product
  // call. Manager / Team Leader / Employee live inside a small set
  // of spaces and benefit from the always-visible sidebar tree;
  // Company Admins manage many workspaces and use the modal's
  // bulk-search instead.

  const [spaceDialogOpen, setSpaceDialogOpen] = useState(false)
  const [hiddenOpen, setHiddenOpen] = useState(false)
  const [archivedOpen, setArchivedOpen] = useState(false)
  // Accordion state for the sidebar sections. `null` = all closed (the
  // initial state). Auto-opens to whichever section owns the current
  // route; clicking a different header swaps it; clicking the open
  // header collapses it.
  const [openSection, setOpenSection] = useState(null)
  const handleSectionToggle = useCallback((id, force) => {
    setOpenSection((prev) => {
      if (force === true) return id
      return prev === id ? null : id
    })
  }, [])
  // Sharing & Permissions opens the SpaceShareDialog with chip input,
  // access-level dropdown, Notify + Message and the People-with-access
  // list. Both this entry point and the assignee picker's "Invite people"
  // item open the same dialog for consistency.
  const [shareSpace, setShareSpace] = useState(null)
  // Space Explorer (the department → spaces picker modal, mounted at
  // the bottom of the layout, plus the inline admin-style Department →
  // Space → List tree below the trigger). COMPANY-WIDE access members
  // only (`tasks.view_workspace`) — they oversee the whole company, so
  // they get the same Space Explorer surface the Company Admin has
  // (button + inline tree) in place of the flat inline Spaces list a
  // regular contributor (incl. department-wide members) sees.
  const [spacesModalOpen, setSpacesModalOpen] = useState(false)
  // Open / closed state for the standalone personal "Spaces" section (company-
  // wide-access members only; mirrors the Company Admin shell). Defaults CLOSED.
  const [personalSpacesOpen, setPersonalSpacesOpen] = useState(false)
  // Which Spaces menu owns the open Space? A personal Space lights up "Spaces";
  // a department Space lights up "Space Explorer".
  const { personalSpaces, personalSpaceIds } = usePersonalSpaces()
  const onAnySpace = pathname.startsWith('/app/spaces/')
  const openSpaceId = pathname.match(/^\/app\/spaces\/([^/]+)/)?.[1] ?? null
  const onPersonalSpace = Boolean(
    openSpaceId && personalSpaceIds.has(String(openSpaceId)),
  )

  // Keep the personal "Spaces" dropdown open whenever the current route is a
  // personal Space — otherwise it collapses the moment a personal list opens.
  // The home (/app) and the Space surface (/app/spaces/:id) render this layout
  // through DIFFERENT route wrappers, so navigating between them REMOUNTS
  // UserLayout and resets `personalSpacesOpen` back to `false`. Keyed on the
  // active Space so a manual collapse is respected until the next navigation.
  // (Mirrors the same self-heal in the Company Admin shell.)
  useEffect(() => {
    if (onPersonalSpace) setPersonalSpacesOpen(true)
  }, [onPersonalSpace, openSpaceId])
  const canExploreAllSpaces = userHasOperation(user, 'tasks.view_workspace')

  // Operation-driven sidebar gating. Each supervision sub-page is
  // controlled by its own `pages.*` operation in the Work Role
  // catalogue. Any role (default or custom) that holds at least one
  // of the four ops gets the supervision section in their sidebar;
  // links within the section render individually based on the exact
  // ops granted.
  //
  // Backward compatibility: company_admin / super_admin pass every
  // operation check inside `hasOperation`, and the legacy seeded
  // defaults (Manager / Team Leader / Employee) carry exactly the
  // same operation sets they ever did — so the visible sidebar is
  // pixel-identical to the pre-Work-Role-Management UX for any user
  // who hasn't been switched to a custom role.
  // The Member Directory (Team Overview) page is reachable with EITHER the
  // page op OR a member-directory scope op — mirrors the backend `me.team`
  // gate (`members.view_workspace \|\| members.view_department \|\| pages.team_overview`)
  // and the reports/leaderboard pattern (`canViewTeamReports` etc.). Without
  // the scope ops here, granting only "View company-wide member directory"
  // left the page unreachable in the UI even though the Work Role allows it.
  const canSeeTeamOverview = useAuthStore((s) =>
    s.hasAnyOperation([
      'pages.team_overview',
      'members.view_workspace',
      'members.view_department',
    ]),
  )
  // Team Tasks / Team Reports pages are reachable with EITHER the page op OR
  // the matching scope op — same root cause + fix as the Member Directory
  // above, and mirrors the backend (`/team/tasks` and `/reports` have no hard
  // op gate; they scope internally by the actor's task/report view op). Without
  // this, granting only "View tasks/reports across the department|company" left
  // the page unreachable in the UI even though the Work Role allows it.
  const canSeeTeamTasks = useAuthStore((s) =>
    s.hasAnyOperation([
      'pages.team_tasks',
      'tasks.view_department',
      'tasks.view_workspace',
    ]),
  )
  const canSeeTeamReports = useAuthStore((s) =>
    s.hasAnyOperation([
      'pages.team_reports',
      'reports.view_department',
      'reports.view_workspace',
    ]),
  )
  // Report Settings is a REPORTS capability, not just a sidebar toggle: the
  // page configures each department's Work Report Task Type / Item Status
  // catalogue. Anyone the backend lets edit it — a department- or
  // workspace-wide reports viewer (`reports.view_department` /
  // `reports.view_workspace`, matching work-report-settings `assertCanEdit`)
  // — must see the link, alongside the `pages.team_reports` sidebar toggle.
  // Restores the pre-regression behaviour where a department-wide member set
  // Type / Status for their team.
  const canViewDeptReports = useAuthStore((s) => s.hasOperation('reports.view_department'))
  const canViewWorkspaceReports = useAuthStore((s) => s.hasOperation('reports.view_workspace'))
  const canManageReportSettings =
    canSeeTeamReports || canViewDeptReports || canViewWorkspaceReports
  // Per-tenant plan feature flags — drives feature-gated sidebar items
  // (SOPs Library, Leaderboard, Reports). Fail-open via the store's
  // `hasFeature` (only an explicit plan `false` hides). Subscribe to the
  // tenant so the rail re-renders when the plan/flags change.
  const tenantFeatures = useAuthStore((s) => s.tenant?.features)
  const hasFeature = (key) => tenantFeatures?.[key] !== false
  // [External Collaboration] Externals are denied tenant-wide enumeration
  // surfaces server-side (the Files & Links page 404s for them); hide any
  // nav item flagged internalOnly so they never land on that broken surface.
  const isExternalMember = useAuthStore((s) => s.user?.membershipType === 'external')
  // Shared renderer for the flat top-level NavLink rows (Dashboard, SOPs
  // Library, Goals, Leaderboard, Our Products, …). Used for every flat group in
  // the rail so the feature/external gate and row styling stay identical no
  // matter where a group is mounted — that's what lets the same items be
  // reordered by simply moving a `renderFlatNav(...)` call in the JSX below.
  const renderFlatNav = (items) =>
    items
      .filter(
        (item) =>
          (!item.requires || hasFeature(item.requires)) &&
          !(item.internalOnly && isExternalMember),
      )
      .map(({ to, label, icon: Icon, end }) => (
        <CollapsedTooltipWrap key={to} label={label} collapsed={collapsed}>
          <NavLink
            to={to}
            end={end}
            // `aria-label` instead of `title` when collapsed — the portal
            // tooltip provides the visible chip; the native `title` tooltip
            // would render alongside it after the browser's ~1.5s delay.
            aria-label={collapsed ? label : undefined}
            className={({ isActive }) =>
              `${ROW_BASE} ${collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'} ${isActive ? ROW_ACTIVE : ROW_INACTIVE}`
            }
          >
            <Icon size={18} />
            {collapsed ? null : label}
          </NavLink>
        </CollapsedTooltipWrap>
      ))
  // Performance moved out of the supervision section (it's now in
  // TOP_NAV_REFERENCE, visible to every user, with the page itself
  // gating the team-analytics surface internally). So the
  // supervision-section visibility no longer needs to factor it in.
  const showSupervisionSection =
    canSeeTeamOverview ||
    canSeeTeamTasks ||
    canManageReportSettings

  // Workspaces — used to scope the Spaces tree. `refetchOnWindowFocus`
  // refreshes the tree whenever the user tabs back into the app, which
  // catches changes made elsewhere (another tab, another teammate) that
  // the socket-driven invalidator might have missed during a brief
  // network blip. `staleTime` of 30s keeps the tree fresh enough for
  // post-acceptance flows where the invitee returns to their tab and
  // expects to see the new workspace immediately.
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
    enabled: Boolean(user),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
  const allWorkspaces = workspacesQuery.data?.data ?? []
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null)
  useEffect(() => {
    if (allWorkspaces.length === 0) return
    const stillExists =
      currentWorkspaceId &&
      allWorkspaces.some((w) => w.id === currentWorkspaceId)
    if (!stillExists) setCurrentWorkspaceId(allWorkspaces[0].id)
  }, [allWorkspaces, currentWorkspaceId])
  const currentWorkspace =
    allWorkspaces.find((w) => w.id === currentWorkspaceId) ??
    allWorkspaces[0] ??
    null
  // [QA-24] Loaded and genuinely empty — not "still loading" / "failed".
  const noWorkspace = workspacesQuery.isSuccess && allWorkspaces.length === 0

  // (Removed the sidebar unread-count query — the header bell owns
  //  its own copy of `['notifications', 'unread-count']` and the
  //  sidebar badge it fed no longer exists.)

  // Spaces in the active workspace. Same auto-refresh policy as the
  // workspaces query above — tabbing back to the app rehydrates the
  // tree, complementing the socket-push invalidations from
  // RealtimeInvalidator for the "I was on another tab when an invite
  // landed" case.
  const spacesQuery = useQuery({
    queryKey: ['spaces', currentWorkspace?.id ?? null],
    queryFn: async () => {
      if (!currentWorkspace) return { data: [] }
      const res = await api.get(`/workspaces/${currentWorkspace.id}/spaces`)
      return spaceListResponse.parse(res.data)
    },
    enabled: Boolean(currentWorkspace),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
  const allSpaces = spacesQuery.data?.data ?? []
  const favorites = useFavoriteSpaces(user?.id)
  // Per-list favourites — separate set + meta map. The Favourites
  // sidebar section below renders BOTH favourited spaces and
  // favourited lists in one combined block. Meta map carries
  // `{ [listId]: { name, spaceId, spaceName } }` so we can render
  // a working name + link without fetching every space's lists.
  const listFavorites = useFavoriteLists(user?.id)
  const [listFavMeta] = useFavoriteListMeta(user?.id)
  const favoriteListRows = Array.from(listFavorites.ids)
    .map((id) => {
      const meta = listFavMeta?.[id]
      if (!meta) return null
      return {
        id,
        name: meta.name,
        spaceId: meta.spaceId,
        spaceName: meta.spaceName ?? null,
      }
    })
    .filter(Boolean)
  const hidden = useHiddenSpaces(user?.id)
  // Spaces tree is an ACCORDION — only one space open at a time. Auto-open
  // the space the user is currently viewing (from the URL); a manual collapse
  // sticks because the effect only re-opens on an actual navigation change.
  const [expandedSpaceId, setExpandedSpaceId] = useExpandedSpace(user?.id)
  // Favourites render the SAME Space tree, but with their own accordion so an
  // open favourite expands under Favourites (not Spaces) — that's how the
  // active state "presents to Favourites, not Spaces".
  const [favExpandedSpaceId, setFavExpandedSpaceId] = useState(null)
  const activeSpaceId = /\/app\/spaces\/([^/]+)/.exec(pathname)?.[1] ?? null
  const prevActiveSpaceId = useRef(null)
  useEffect(() => {
    if (activeSpaceId && activeSpaceId !== prevActiveSpaceId.current) {
      // A favourited active Space auto-expands under FAVOURITES and collapses in
      // Spaces; a non-favourite auto-expands in Spaces as before.
      if (favorites.has(activeSpaceId)) {
        setFavExpandedSpaceId(activeSpaceId)
        setExpandedSpaceId((prev) => (prev === activeSpaceId ? null : prev))
      } else {
        setExpandedSpaceId(activeSpaceId)
      }
    }
    prevActiveSpaceId.current = activeSpaceId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpaceId, favorites])
  const spacesForWorkspace = allSpaces.filter(
    (s) => !s.isArchived && !hidden.has(s.id),
  )
  const hiddenSpacesForWorkspace = allSpaces.filter(
    (s) => !s.isArchived && hidden.has(s.id),
  )
  const favoriteSpaces = allSpaces.filter(
    (s) => !s.isArchived && favorites.has(s.id),
  )
  const archivedSpaces = allSpaces.filter((s) => s.isArchived)

  // Route prefixes owned by the Favourites section: each favourited Space (and
  // any List/Project inside it) plus each individually favourited List. Drives
  // BOTH the Favourites section's active highlight / auto-open AND the Spaces
  // section's exclusion — so opening a favourite lights up Favourites, not
  // Spaces. Navigation targets are unchanged, so the Space page still opens and
  // creates Projects / Lists / Tasks exactly as before.
  const favoriteMatchPaths = [
    ...favoriteSpaces.map((s) => `/app/spaces/${s.id}`),
    ...favoriteListRows.map((r) => `/app/spaces/${r.spaceId}/lists/${r.id}`),
  ]

  // Drag-and-drop in the Spaces tree. MouseSensor keeps desktop behaviour
  // identical — 6px activation distance so a plain click still navigates.
  // TouchSensor is delay-based so a swipe scrolls the sidebar while a
  // press-and-hold starts a drag-reorder.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
  )
  async function handleDragEnd(event) {
    const drag = event.active.data.current
    let drop = event.over?.data.current
    if (!drag || !drop) return

    if (drop.kind === 'space') {
      if (drag.fromSpaceId === drop.spaceId && drag.fromFolderId === null)
        return
    } else if (drop.kind === 'folder') {
      if (drag.fromFolderId === drop.folderId) return
      if (drag.kind === 'folder') return
    } else if (drop.kind === 'list-reorder') {
      if (drag.kind !== 'list') return
      if (drag.id === drop.listId) return
      if (drag.fromSpaceId !== drop.spaceId) {
        drop = { kind: 'space', spaceId: drop.spaceId }
      } else {
        try {
          const cached = qc.getQueryData(['lists', drop.spaceId])
          if (!cached?.data) return
          const orderedIds = cached.data.map((l) => l.id)
          const fromIdx = orderedIds.indexOf(drag.id)
          const toIdx = orderedIds.indexOf(drop.listId)
          if (fromIdx === -1 || toIdx === -1) return
          const desired = drop.position === 'after' ? toIdx + 1 : toIdx
          const insertAt = fromIdx < desired ? desired - 1 : desired
          if (insertAt === fromIdx) return
          const next = orderedIds.slice()
          next.splice(fromIdx, 1)
          next.splice(insertAt, 0, drag.id)
          qc.setQueryData(['lists', drop.spaceId], (old) => {
            if (!old) return old
            const byId = new Map(old.data.map((l) => [l.id, l]))
            return {
              ...old,
              data: next.map((id) => byId.get(id)).filter(Boolean),
            }
          })
          await api.post(`/spaces/${drop.spaceId}/lists/reorder`, {
            listIds: next,
          })
          toast.success('Lists reordered.')
        } catch (err) {
          toast.error(extractApiError(err).message)
          void qc.invalidateQueries({ queryKey: ['lists', drop.spaceId] })
        }
        return
      }
    }

    try {
      if (drag.kind === 'folder') {
        if (drop.kind !== 'space') return
        await api.patch(`/spaces/${drag.fromSpaceId}/folders/${drag.id}`, {
          spaceId: drop.spaceId,
        })
        toast.success('Project moved successfully.')
        void qc.invalidateQueries({ queryKey: ['folders', drag.fromSpaceId] })
        void qc.invalidateQueries({ queryKey: ['folders', drop.spaceId] })
        void qc.invalidateQueries({ queryKey: ['lists', drag.fromSpaceId] })
        void qc.invalidateQueries({ queryKey: ['lists', drop.spaceId] })
        void qc.invalidateQueries({
          queryKey: ['documents', drag.fromSpaceId],
        })
        void qc.invalidateQueries({ queryKey: ['documents', drop.spaceId] })
      } else if (drag.kind === 'list') {
        const body =
          drop.kind === 'folder'
            ? { spaceId: drop.spaceId, folderId: drop.folderId }
            : { spaceId: drop.spaceId, folderId: null }
        await api.patch(`/spaces/${drag.fromSpaceId}/lists/${drag.id}`, body)
        toast.success('List moved successfully.')
        void qc.invalidateQueries({ queryKey: ['lists', drag.fromSpaceId] })
        void qc.invalidateQueries({ queryKey: ['lists', drop.spaceId] })
      } else if (drag.kind === 'document') {
        const body =
          drop.kind === 'folder'
            ? { spaceId: drop.spaceId, folderId: drop.folderId }
            : { spaceId: drop.spaceId, folderId: null }
        await api.patch(`/documents/${drag.id}`, body)
        toast.success('Document moved successfully.')
        void qc.invalidateQueries({
          queryKey: ['documents', drag.fromSpaceId],
        })
        void qc.invalidateQueries({ queryKey: ['documents', drop.spaceId] })
        void qc.invalidateQueries({ queryKey: ['document', drag.id] })
      }
    } catch (err) {
      toast.error(extractApiError(err).message)
    }
  }

  async function handleSignOut() {
    await signOut()
    toast.success('Signed out successfully.')
    navigate('/sign-in', { replace: true })
  }

  return (
    <div className="taskmagix-page-bg h-screen flex overflow-hidden">
      {/* Sidebar — diagonal navy→cyan gradient. Identical structure to
       *  AdminLayout. CSS class scopes ink + rule tokens to white so
       *  every NavLink, divider and chip inside lands legible on the
       *  dark gradient automatically. */}
      {/* Mobile drawer backdrop — taps to close. Hidden on desktop (lg+). */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      ) : null}
      <aside
        data-tour="app-sidebar"
        className={
          'taskmagix-sidebar shrink-0 border-r border-[color:var(--color-rule)] text-[color:var(--color-ink)] text-[length:calc(var(--font-size-scale,1)*14px)] font-medium flex flex-col ' +
          // Mobile: off-canvas drawer; desktop (lg+): exact in-flow sidebar
          // via lg: resets, so desktop is unchanged.
          'fixed inset-y-0 left-0 z-50 lg:relative lg:inset-auto lg:z-auto ' +
          'transition-[transform,width] duration-200 ease-out ' +
          (mobileOpen ? 'translate-x-0 ' : '-translate-x-full ') +
          'lg:translate-x-0 ' +
          (collapsed ? 'w-[68px]' : 'w-[280px]')
        }
      >
        {/* Edge toggle — floating circle on the sidebar's right edge.
         *  Matches AdminLayout + SuperAdminLayout. */}
        <SidebarToggle placement="edge" />

        <div
          className={
            'h-[84px] flex items-center justify-center border-b border-[color:var(--color-rule)] ' +
            (collapsed ? 'px-2' : 'px-4')
          }
        >
          {/* Collapsed rail keeps a fitted logo — hiding it entirely
           *  (the old `collapsed ? null : …`) left a blank header block
           *  AND dropped the go-home click target (QA-reported). The
           *  platform logo is an admin-uploaded image of arbitrary
           *  aspect ratio, so `object-contain` inside h-9 × 52px fits
           *  wide wordmarks and square marks alike — no cropping. */}
          {collapsed ? (
            <Link
              to="/app"
              aria-label="Go to home"
              className="inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <BrandMarkIcon />
            </Link>
          ) : (
            <Link
              to="/app"
              aria-label="Go to home"
              className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]/30 transition-opacity hover:opacity-90"
            >
              <Logo size="xl" className="p-1.5" />
            </Link>
          )}
        </div>

        <nav
          className={
            // Top fade-mask: rows fade to transparent before reaching the
            // header divider, so a scrolled list never reads as "touching"
            // the line under the logo. The fade height matches pt-6 (24px)
            // so at rest the first row sits clear of it and nothing looks
            // dimmed. Standing rule: this mask lives identically in all
            // three sidebar layouts (Admin / User / SuperAdmin).
            'flex-1 min-h-0 overflow-y-auto pt-6 pb-4 space-y-1.5 [mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0,black_24px,black_100%)] [scrollbar-width:thin] [scrollbar-color:var(--color-rule)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[color:var(--color-rule)] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[color:var(--color-ink-muted)] ' +
            (collapsed ? 'px-2' : 'px-4')
          }
        >
          {/* Daily-action items — just Dashboard (orientation) at the top of
           *  the rail. Goals moved down to the bottom cluster with the Reports
           *  section per product request. */}
          {renderFlatNav(TOP_NAV_DAILY)}

          {/* ─── Favorites — moved up to the 2nd sidebar section ──────
           *
           *  Pinned spaces the user reaches most often. Sits right
           *  after Dashboard so the first thing the user sees in the
           *  sidebar after the home link is their own curated list,
           *  with the full Spaces tree just below it as section 3.
           *  The whole section renders ONLY when at least one Space or
           *  List is favourited — mirroring the Archived group, which
           *  also appears only when it has records (QA-requested
           *  parity). The old always-visible "No favourites yet."
           *  empty state traded that consistency for discoverability;
           *  the star actions on Space/List rows remain the way users
           *  find the feature, and the section appears the moment the
           *  first favourite is added. */}
          {favoriteSpaces.length > 0 || favoriteListRows.length > 0 ? (
          <SidebarSection
            id="favorites"
            title="Favourites"
            icon={Star}
            matchPaths={favoriteMatchPaths}
            headerActive={false}
            openSection={openSection}
            onToggle={handleSectionToggle}
          >
              <>
                {/* Favourited Spaces — render first, then any favourited
                 *  individual Lists. Each favourited Space is the SAME
                 *  interactive tree node used under Spaces (expand to
                 *  Projects → Lists, 3-dot actions, active-List highlight,
                 *  drag-and-drop) so a favourite "works like Spaces". Wrapped
                 *  in its own DndContext (SpaceChildren needs one) and driven
                 *  by the favourites-only accordion `favExpandedSpaceId`. A
                 *  Space and one of its Lists can both be favourited — that's
                 *  intentional, pinned at different scopes. */}
                {currentWorkspace && favoriteSpaces.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragEnd={handleDragEnd}
                  >
                    {favoriteSpaces.map((space) => (
                      <SidebarSpaceNode
                        key={`space-${space.id}`}
                        space={space}
                        workspaceId={currentWorkspace.id}
                        expanded={favExpandedSpaceId === space.id}
                        onToggleExpand={() =>
                          setFavExpandedSpaceId(
                            favExpandedSpaceId === space.id ? null : space.id,
                          )
                        }
                        onShare={() =>
                          setShareSpace({ id: space.id, name: space.name })
                        }
                      />
                    ))}
                  </DndContext>
                ) : null}
                {favoriteListRows.map((row) => (
                  <NavLink
                    key={`list-${row.id}`}
                    to={`/app/spaces/${row.spaceId}/lists/${row.id}`}
                    title={
                      row.spaceName
                        ? `${row.name} · in ${row.spaceName}`
                        : row.name
                    }
                    className={({ isActive }) =>
                      'flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm transition-colors cursor-pointer border ' +
                      (isActive
                        ? 'bg-white/15 text-white border-white/25 shadow-sm'
                        : 'text-white/65 hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] border-transparent')
                    }
                  >
                    <ListChecks
                      size={14}
                      className="shrink-0 text-[color:var(--color-ink-muted)]"
                    />
                    <span className="truncate flex-1">{row.name}</span>
                  </NavLink>
                ))}
              </>
          </SidebarSection>
          ) : null}

          {/* ─── Spaces — INLINE tree, reverted from the SpacesModal.
           *  User-shell roles (Manager / Team Leader / Employee) keep
           *  the original in-sidebar collapsible tree. AdminLayout
           *  (Company Admin / Sub Admin) gets the modal experience.
           *  This split was a product call: admins manage many
           *  workspaces and benefit from the modal's bulk search;
           *  individual contributors live inside a handful of spaces
           *  and prefer the always-visible inline tree.
           *
           *  The modal IS now also surfaced here — additively and
           *  scoped: company-wide-access members get a "Space Explorer"
           *  button (below) that opens `<SpacesModal />` ALONGSIDE this
           *  inline tree (it doesn't replace it). Everyone else keeps
           *  only the inline tree. */}

          {/* Company-wide-access members get the SAME two Spaces menus as the
           *  Company Admin shell: a personal "Spaces" section (the member's own
           *  private Spaces via `<AdminSpacesTree />`) and a "Space Explorer"
           *  button that opens the department → Space picker modal. No
           *  `aria-current` on the Explorer button: it has no route, and
           *  setting it would paint a stray accent bar via the global
           *  `aside [aria-current='page']::before` rule. */}
          {canExploreAllSpaces ? (
            <>
              {/* "Spaces" — the member's OWN personal, private Spaces, exactly
               *  like the Company Admin shell. Collapsible; expands to the
               *  inline personal-spaces tree with "+ New Space". Separate menu
               *  from "Space Explorer" (department / company Spaces) below. */}
              {collapsed ? (
                <CollapsedTooltipWrap label="Personal Spaces" collapsed={collapsed}>
                  <button
                    type="button"
                    // Independent of Space Explorer — jumps to the first
                    // personal Space rather than opening the Explorer modal.
                    onClick={() =>
                      personalSpaces[0] &&
                      navigate(`/app/spaces/${personalSpaces[0].id}`)
                    }
                    aria-label="Personal Spaces"
                    aria-current={onPersonalSpace ? 'page' : undefined}
                    className={`${ROW_BASE} px-0 py-2.5 justify-center w-full ${onPersonalSpace ? ROW_ACTIVE : ROW_INACTIVE}`}
                  >
                    <FolderTree size={18} className="shrink-0" />
                  </button>
                </CollapsedTooltipWrap>
              ) : (
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setPersonalSpacesOpen((v) => !v)}
                    aria-expanded={personalSpacesOpen}
                    aria-current={onPersonalSpace ? 'page' : undefined}
                    className={`${ROW_BASE} px-3 py-2.5 w-full ${onPersonalSpace ? ROW_ACTIVE : ROW_INACTIVE}`}
                  >
                    <FolderTree size={18} className="shrink-0" />
                    <span className="flex-1 truncate text-left">
                      Personal Spaces
                    </span>
                    <ChevronDown
                      size={14}
                      className={
                        'shrink-0 transition-transform duration-150 ' +
                        (personalSpacesOpen ? '' : '-rotate-90')
                      }
                    />
                  </button>
                  {personalSpacesOpen ? (
                    <AdminSpacesTree collapsed={collapsed} />
                  ) : null}
                </div>
              )}

              {/* Space Explorer — the department → Space picker modal. Active
               *  only on a DEPARTMENT Space (a personal Space lights up
               *  "Spaces" above instead). */}
              <CollapsedTooltipWrap label="Space Explorer" collapsed={collapsed}>
                <button
                  type="button"
                  data-tour="nav-spaces"
                  onClick={() => setSpacesModalOpen(true)}
                  aria-label={collapsed ? 'Space Explorer' : undefined}
                  aria-current={onAnySpace && !onPersonalSpace ? 'page' : undefined}
                  className={`${ROW_BASE} ${collapsed ? 'px-0 py-2.5 justify-center w-full' : 'px-3 py-2.5 w-full'} ${onAnySpace && !onPersonalSpace ? ROW_ACTIVE : ROW_INACTIVE}`}
                >
                  <Layers size={18} className="shrink-0" />
                  {collapsed ? null : (
                    <span className="flex-1 truncate text-left">
                      Space Explorer
                    </span>
                  )}
                </button>
              </CollapsedTooltipWrap>
              {/* Inline workspace → Space → Project → List tree (the original
               *  Space Explorer). Renders only when INSIDE a department Space
               *  and the rail isn't collapsed; the button above still opens the
               *  department picker modal. Same component the Admin shell uses. */}
              {!collapsed ? <AdminCurrentSpaceTree /> : null}
            </>
          ) : null}

          {/* Inline Spaces tree — hidden for company-wide members, who browse
           *  via the "Space Explorer" button above instead (it shows the same
           *  spaces grouped by department, without a long flat sidebar list).
           *  Regular members keep this always-visible tree. */}
          {!canExploreAllSpaces ? (
          <SidebarSection
            id="spaces"
            dataTour="nav-spaces"
            title="Spaces"
            icon={Layers}
            matchPaths={['/app/spaces']}
            excludePaths={favoriteMatchPaths}
            openSection={openSection}
            onToggle={handleSectionToggle}
            /* `actions={[...]}` (inline `+ New Space` button on the
             *  section header) removed by request 2026-05-22. The
             *  header now only carries its dropdown chevron — creating
             *  a space is still reachable via the `+ New Space` row
             *  rendered at the bottom of the spaces list and via the
             *  `+` action on any existing space row's hover cluster. */
          >
            {spacesQuery.isLoading ? (
              <p className="px-3 py-1.5 text-sm italic text-[color:var(--color-ink-subtle)]">
                Loading spaces…
              </p>
            ) : noWorkspace ? (
              // [QA-24] A member who hasn't been added to any workspace can't
              // create one here — say what to do instead of "No spaces yet".
              <p className="px-3 py-1.5 text-sm text-[color:var(--color-ink-subtle)]">
                You haven&apos;t been added to a workspace yet. Ask your
                Company Admin to add you.
              </p>
            ) : spacesForWorkspace.length === 0 ? (
              <p className="px-3 py-1.5 text-sm italic text-[color:var(--color-ink-subtle)]">
                No spaces yet.
              </p>
            ) : null}

            {currentWorkspace ? (
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragEnd={handleDragEnd}
              >
                {spacesForWorkspace.map((space) => (
                  <SidebarSpaceNode
                    key={space.id}
                    space={space}
                    workspaceId={currentWorkspace.id}
                    expanded={expandedSpaceId === space.id}
                    onToggleExpand={() =>
                      setExpandedSpaceId(
                        expandedSpaceId === space.id ? null : space.id,
                      )
                    }
                    onShare={() =>
                      setShareSpace({ id: space.id, name: space.name })
                    }
                  />
                ))}
              </DndContext>
            ) : null}

            {/* + New Space row — quick-add at the bottom of the tree.
             *  [QA-24] Hidden when the member has no workspace: the old toast
             *  told them to "Create a workspace first.", which a member can't. */}
            {noWorkspace ? null : (
            <button
              type="button"
              onClick={() => {
                if (!currentWorkspace) return // workspaces still loading
                setSpaceDialogOpen(true)
              }}
              className="w-full flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm text-left cursor-pointer transition-colors text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] border border-transparent"
            >
              <Plus size={15} strokeWidth={1.75} className="shrink-0" />
              New Space
            </button>
            )}

            {/* Hidden group — the per-user "I tucked these away" list. */}
            {hiddenSpacesForWorkspace.length > 0 ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setHiddenOpen((v) => !v)}
                  aria-expanded={hiddenOpen}
                  className="w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-xs uppercase tracking-[0.12em] font-semibold text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors"
                >
                  <ChevronDown
                    size={12}
                    strokeWidth={2.25}
                    className={
                      'shrink-0 transition-transform duration-150 ' +
                      (hiddenOpen ? '' : '-rotate-90')
                    }
                  />
                  {/* Group icon — matches the Eye/EyeOff iconography the
                   *  hide/unhide actions use, same treatment as the
                   *  Archived group below so the two utility groups and
                   *  the main nav all carry module icons. */}
                  <EyeOff size={12} strokeWidth={2.25} className="shrink-0" />
                  <span className="truncate flex-1 text-left">
                    Hidden ({hiddenSpacesForWorkspace.length})
                  </span>
                </button>
                {hiddenOpen
                  ? hiddenSpacesForWorkspace.map((space) => (
                      <div
                        key={`hidden-${space.id}`}
                        className="group/hidden relative flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm text-[color:var(--color-ink-muted)] opacity-70 hover:opacity-100 hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] border border-transparent transition-colors"
                      >
                        <SpaceAvatar
                          name={space.name}
                          color={space.color ?? null}
                        />
                        <span className="truncate flex-1" title={space.name}>{space.name}</span>
                        <button
                          type="button"
                          aria-label={`Unhide ${space.name}`}
                          title="Unhide"
                          onClick={() => {
                            hidden.remove(space.id)
                            toast.success(
                              `"${space.name}" restored to sidebar.`,
                            )
                          }}
                          className="p-1 rounded text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer opacity-100 lg:opacity-0 lg:group-hover/hidden:opacity-100 transition-opacity"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    ))
                  : null}
              </div>
            ) : null}

            {/* Archived group — soft-deleted Spaces. Listed under the
             *  Hidden group so the user can find and Restore them
             *  without trawling the database. Restore flips the
             *  `isArchived` flag back to false; cascade undo isn't
             *  needed because Archive never removes child data. */}
            {archivedSpaces.length > 0 ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setArchivedOpen((v) => !v)}
                  aria-expanded={archivedOpen}
                  className="w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-xs uppercase tracking-[0.12em] font-semibold text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors"
                >
                  <ChevronDown
                    size={12}
                    strokeWidth={2.25}
                    className={
                      'shrink-0 transition-transform duration-150 ' +
                      (archivedOpen ? '' : '-rotate-90')
                    }
                  />
                  {/* Group icon — the same lucide Archive glyph the
                   *  space menu's Archive action uses, so the action and
                   *  the destination group read as one concept. */}
                  <Archive size={12} strokeWidth={2.25} className="shrink-0" />
                  <span className="truncate flex-1 text-left">
                    Archived ({archivedSpaces.length})
                  </span>
                </button>
                {archivedOpen
                  ? archivedSpaces.map((space) => (
                      <div
                        key={`archived-${space.id}`}
                        className="group/archived relative flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm text-[color:var(--color-ink-muted)] opacity-70 hover:opacity-100 hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] border border-transparent transition-colors"
                      >
                        <SpaceAvatar
                          name={space.name}
                          color={space.color ?? null}
                        />
                        <span className="truncate flex-1" title={space.name}>{space.name}</span>
                        <button
                          type="button"
                          aria-label={`Restore ${space.name}`}
                          title="Restore"
                          onClick={async () => {
                            try {
                              await api.patch(
                                `/workspaces/${currentWorkspace?.id ?? space.workspaceId}/spaces/${space.id}`,
                                { isArchived: false },
                              )
                              toast.success('Space restored successfully.')
                              void qc.invalidateQueries({
                                queryKey: ['spaces', currentWorkspace?.id ?? space.workspaceId],
                              })
                              void qc.invalidateQueries({
                                queryKey: ['space', space.id],
                              })
                            } catch (err) {
                              toast.error(extractApiError(err).message)
                            }
                          }}
                          className="p-1 rounded text-[color:var(--color-ink-subtle)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer opacity-100 lg:opacity-0 lg:group-hover/archived:opacity-100 transition-opacity"
                        >
                          <ArchiveRestore size={14} />
                        </button>
                      </div>
                    ))
                  : null}
              </div>
            ) : null}
          </SidebarSection>
          ) : null}

          {/* Reference items — SOPs Library + Files & Links, mounted directly
           *  under the Spaces tree. Leaderboard + Our Products moved down to
           *  the bottom cluster (below the Reports section). Feature-gated
           *  entries (SOPs → `sop_integration`) drop out when the plan
           *  excludes them. */}
          {/* Company-wide members get SOPs Library INSIDE the "Process" group
           *  (mirroring the Company Admin layout), so drop it from this flat
           *  reference row for them; everyone else keeps it flat. */}
          {renderFlatNav(
            canExploreAllSpaces
              ? TOP_NAV_REFERENCE.filter((i) => i.to !== '/app/sops-library')
              : TOP_NAV_REFERENCE,
          )}

{/* Spaces section relocated — see the SidebarSection mounted
           *  right after Favorites above (between Favorites and the
           *  Reference items). Visible sidebar order: Dashboard →
           *  Favorites → Spaces → SOPs Library → Files & Links →
           *  Performance → Manager → Personal → Reports. */}

          {/* ─── Role-specific supervision section ───────────────────
           *
           *  Promoted ABOVE Favorites/Spaces because for a Manager
           *  (workspace-wide oversight) or Team Leader (department-
           *  scoped oversight) this is the primary work surface —
           *  "what's the team doing right now?" comes before drilling
           *  into a specific Space. Employees see neither and fall
           *  through to the personal sections below.
           *
           *   Manager      → workspace-wide team supervision
           *   Team Leader  → department-scoped supervision
           *
           *  Both sections only carry team-oversight pages. Personal
           *  work (My Tasks / Timesheet / Reports) is always exposed
           *  via the "Personal" group below so a Manager who also has
           *  individual tasks still finds them in the same place.
           */}
          {/* Supervision section — a single block that renders the
           *  subset of /app/workspace-overview, /app/team-tasks,
           *  /app/performance, /app/reports/team that the user's
           *  effective Work Role operations grant. The section header
           *  carries whichever label best matches the page mix granted
           *  to this role; a custom role with only Team Tasks ticked
           *  still sees "Supervision" without misleading "Manager" /
           *  "Team Leader" copy. */}
          {showSupervisionSection ? (
            <SidebarSection
              id="supervision"
              dataTour="nav-supervision"
              /* Company-wide members see this group as "Process" (mirroring the
               *  Company Admin's sidebar organization), holding their existing
               *  company-wide pages. Department-scoped supervisors keep
               *  "Supervision" (their own team). No access changes either way. */
              title={canExploreAllSpaces ? 'Process' : 'Supervision'}
              icon={
                canExploreAllSpaces
                  ? Workflow
                  : canSeeTeamOverview
                    ? UserCog
                    : Users
              }
              openSection={openSection}
              onToggle={handleSectionToggle}
              /* EVERY child route this section renders must be listed here.
               * `matchPaths` drives `routeOwned`, which does two jobs: it
               * auto-opens the section and it paints the header active. A
               * child whose path is missing therefore lands the user on a page
               * with the section COLLAPSED and nothing highlighted — they lose
               * their place in the nav. `/app/team-analytics` and
               * `/app/report-settings` were both absent while being rendered
               * as children below. */
              matchPaths={[
                '/app/team-overview',
                '/app/team-tasks',
                '/app/team-analytics',
                '/app/reports/team',
                // Report Settings lives under Supervision for DEPARTMENT-scoped
                // members (they configure their own department's catalogue).
                // Company-wide members get it under Reports instead (self-only
                // company-wide fallback), so it's not owned by this section.
                ...(canViewWorkspaceReports ? [] : ['/app/report-settings']),
                // SOPs Library sits inside this "Process" group for company-wide
                // members (a flat reference row for everyone else), so the group
                // auto-opens + highlights when they're on it.
                ...(canExploreAllSpaces ? ['/app/sops-library'] : []),
              ]}
            >
              {/* SOPs Library leads the Process group for company-wide members
               *  (matches the admin's Process). Same sop_integration feature
               *  gate as the flat reference row it moved from — no access change. */}
              {canExploreAllSpaces && hasFeature('sop_integration') ? (
                <SidebarChildLink
                  to="/app/sops-library"
                  label="SOPs Library"
                  icon={BookOpen}
                />
              ) : null}
              {canSeeTeamOverview ? (
                <SidebarChildLink
                  to="/app/team-overview"
                  label="Team Overview"
                  icon={Eye}
                />
              ) : null}
              {canSeeTeamTasks ? (
                <SidebarChildLink
                  to="/app/team-tasks"
                  label="Team Tasks"
                  icon={Users}
                />
              ) : null}
              {/* Team Analytics — task progress + creation-source breakdown +
               *  per-employee day audit. Same visibility as Team Tasks
               *  (department- / workspace-wide task view); the page + API
               *  enforce the real gate. */}
              {canSeeTeamTasks ? (
                <SidebarChildLink
                  to="/app/team-analytics"
                  label="Team Analytics"
                  icon={BarChart3}
                />
              ) : null}
              {/* Performance link was moved out of this supervision
               *  section and into TOP_NAV_REFERENCE when Achievements
               *  + Performance merged into one role-adaptive page. The
               *  page itself now decides whether to render the team
               *  analytics surface based on the actor's operations,
               *  so a single sidebar entry covers both audiences. */}
              {canSeeTeamReports ? (
                <SidebarChildLink
                  to="/app/reports/team"
                  label="Team Reports"
                  icon={FileBarChart}
                />
              ) : null}
              {/* Report Settings — department-scoped editors (Team Leader /
               *  department-wide member via `reports.view_department` or the
               *  `pages.team_reports` toggle) configure THEIR department's Task
               *  Type / Item Status here, a team-supervision action. Company-wide
               *  members (`reports.view_workspace`) get it under Reports instead
               *  — it's self-only (the company-wide fallback) for them. Externals
               *  never configure a company's report catalogues. */}
              {canManageReportSettings && !canViewWorkspaceReports ? (
                <SidebarChildLink
                  to="/app/report-settings"
                  label="Report Settings"
                  icon={SettingsIcon}
                />
              ) : null}
            </SidebarSection>
          ) : null}

{/* Favorites + Spaces sections both moved up — see the
           *  SidebarSections mounted right after Dashboard and the
           *  Reference items above. Comment kept as a breadcrumb so
           *  anyone scrolling this file looking for the pinned-spaces
           *  list knows where they migrated to. */}

          {/* ─── "Personal" collapsible group removed ─────────────────
           *  Timesheet was first hidden from the sidebar; on
           *  2026-05-27 the entire Time Tracking module was retired —
           *  the `/app/timesheet` route is unmounted in App.jsx, and
           *  the page component sits orphaned for reference only.
           *  Leaderboard was promoted to a flat top-level item via
           *  `TOP_NAV_REFERENCE` above, so the collapsible group no
           *  longer had anything to hold. */}

          {/* Goals — first row of the product-ordered bottom cluster, sitting
           *  directly ABOVE the Reports section:
           *  Goals → Reports → Leaderboard → Our Products → User Manual. */}
          {renderFlatNav(BOTTOM_NAV_ABOVE_REPORTS)}

          {/* ─── Personal Reports — Daily / Weekly / Monthly cadences.
           *  Every role submits their own reports. Team Reports for
           *  Manager / Team Leader live in their respective sections. */}
          <SidebarSection
            id="reports"
            title="Reports"
            icon={FileBarChart}
            openSection={openSection}
            onToggle={handleSectionToggle}
            matchPaths={[
              '/app/reports/daily',
              '/app/reports/weekly',
              '/app/reports/monthly',
              // Company-wide members manage the company-wide report catalogue
              // here (self-only); department-scoped members keep it under
              // Supervision, so only this cohort's section owns the path.
              ...(canViewWorkspaceReports ? ['/app/report-settings'] : []),
            ]}
          >
            <SidebarChildLink
              to="/app/reports/daily"
              label="Daily Report"
              icon={ClipboardList}
            />
            <SidebarChildLink
              to="/app/reports/weekly"
              label="Weekly Report"
              icon={CalendarDays}
            />
            <SidebarChildLink
              to="/app/reports/monthly"
              label="Monthly Report"
              icon={CalendarRange}
            />
            {/* Report Settings — ONLY for company-wide members, who manage the
             *  company-wide fallback catalogue (self-only). Moved here from
             *  Supervision per product decision; department-scoped members still
             *  get it under Supervision for their own department. */}
            {canManageReportSettings && canViewWorkspaceReports ? (
              <SidebarChildLink
                to="/app/report-settings"
                label="Report Settings"
                icon={SettingsIcon}
              />
            ) : null}
          </SidebarSection>

          {/* Leaderboard + Our Products — the rows BELOW the Reports section in
           *  the product-ordered bottom cluster, directly above User Manual:
           *  Goals → Reports → Leaderboard → Our Products → User Manual. */}
          {renderFlatNav(BOTTOM_NAV_BELOW_REPORTS)}

          {/* User Manual — the role-based guide. Kept as the very last item
           *  in the rail, below the Reports section. Flat NavLink, matching
           *  the TOP_NAV rows; no gate (visible to every member). */}
          <CollapsedTooltipWrap label="User Manual" collapsed={collapsed}>
            <NavLink
              to="/app/user-manual"
              aria-label={collapsed ? 'User Manual' : undefined}
              className={({ isActive }) =>
                `${ROW_BASE} ${collapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'} ${isActive ? ROW_ACTIVE : ROW_INACTIVE}`
              }
            >
              <BookOpen size={18} />
              {collapsed ? null : 'User Manual'}
            </NavLink>
          </CollapsedTooltipWrap>
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 text-[color:var(--color-ink)]">
        <header
          className="h-[64px] border-b border-[color:var(--color-rule)] px-4 lg:px-8 flex items-center justify-between gap-2 bg-[color:var(--color-card)]"
          style={{ fontSize: 'calc(var(--text-sm) + 1px)' }}
        >
          {/* Left group — live platform clock pinned to the far left so
           *  the eye reads the current date/time first. The (currently
           *  hidden) language selector also lives here as a global,
           *  app-wide control, separate from the right-side cluster. */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            {/* Hamburger — opens the sidebar drawer on mobile / tablet;
             *  hidden on desktop (lg+). */}
            <button
              type="button"
              onClick={openMobile}
              aria-label="Open menu"
              className="lg:hidden -ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Menu size={20} />
            </button>
            {/* Live date/time pill. Hidden on mobile / tablet (widest header
             *  item, informational); shown on desktop (lg+) as before. */}
            <div className="hidden lg:block">
              <DateTimeClock />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-4">
            {/* Quick jump to Focus Board — same Target icon the
             *  sidebar uses so the affordance is recognisable. The
             *  link uses NavLink instead of a button so the user
             *  gets browser-history / middle-click behaviour for
             *  free, and so the active-state styling kicks in when
             *  they're already on the page. */}
            <NavLink
              to="/app/focus-board"
              title="Focus Board"
              data-tour="nav-tasks"
              className={({ isActive }) =>
                'inline-flex shrink-0 items-center gap-1.5 h-9 w-9 justify-center px-0 sm:w-auto sm:px-3 sm:justify-start rounded-full border text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ' +
                (isActive
                  ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
                  : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]')
              }
            >
              <Target size={14} className="shrink-0" />
              {/* Label hidden on phones (icon-only pill) so it never wraps in
               *  the crowded mobile header; shown from sm up. */}
              <span className="hidden sm:inline">Focus Board</span>
            </NavLink>
            {/* End-of-day (moon) and quick-create (+) buttons are
             *  hidden by request. The components stay imported and
             *  wired so re-enabling is a one-line revert. */}
            {/* EndOfDayButton was a Time-Tracking wrap-up dialog,
             *  removed 2026-05-27. QuickCreateButton stays gated off
             *  via the existing `false ?` pattern. */}
            {SHOW_QUICK_CREATE ? <QuickCreateButton /> : null}
            {/* Accessibility text-size control — Compact / Medium /
             *  Large. Sits before the bell so the more frequently
             *  used controls (notifications, profile) stay in the
             *  rightmost slots where the eye expects them. */}
            <ThemeToggle />
            <FontSizeMenu />
            {/* Notifications bell — at-a-glance unread count plus a
             *  dropdown list of recent items. Each row deep-links to
             *  its target page; the user's Inbox is still available
             *  via the sidebar for full triage + the Sent box. */}
            <NotificationsBell />
            {/* Company switcher — the company pill is a dropdown of
             *  every company this person belongs to, so they can switch
             *  organisation in one click. Sits right after the bell,
             *  beside their own profile menu. */}
            <OrganisationSwitcher />
            <div
              className="h-8 w-px bg-[color:var(--color-rule)]"
              aria-hidden="true"
            />
            {user ? (
              <UserMenu
                user={user}
                departmentName={currentWorkspace?.departmentName ?? null}
                onSignOut={handleSignOut}
              />
            ) : null}
          </div>
        </header>

        <main ref={mainRef} className="app-main-scroll flex-1 overflow-auto bg-[color:var(--color-bg)]">
          <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {/* Scope a page-render crash to the CONTENT area only. Without this,
             *  any thrown error bubbles to the single root boundary (main.jsx)
             *  which replaces the WHOLE shell — so one bad page took the sidebar
             *  + Space Explorer down too. Keyed by route so navigating away
             *  remounts the boundary and recovers automatically. */}
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* Space dialogs launched from the sidebar tree */}
      <SpaceCreateDialog
        open={spaceDialogOpen}
        workspaceId={currentWorkspace?.id ?? null}
        onClose={() => setSpaceDialogOpen(false)}
        onCreated={(spaceId) => navigate(`/app/spaces/${spaceId}`)}
      />
      <SpaceShareDialog
        open={Boolean(shareSpace)}
        spaceId={shareSpace?.id ?? null}
        workspaceId={currentWorkspace?.id ?? null}
        spaceName={shareSpace?.name ?? ''}
        isPersonal={shareSpace?.isPersonal === true}
        onClose={() => setShareSpace(null)}
      />
      {/* Space Explorer modal — opened from the sidebar trigger above
       *  the inline Spaces tree (company-wide-access members only). */}
      <SpacesModal
        open={spacesModalOpen}
        onClose={() => setSpacesModalOpen(false)}
      />
      {/* First-login guided product tour (regular User). Renders nothing unless
       *  the persisted tour state is not_started, or they restart it. */}
      <ProductTour />

    </div>
  )
}

// ----------------------------------------------------------------------
// Coloured square avatar for a Space — first letter, palette derived
// deterministically so the same name always gets the same colour.
// ----------------------------------------------------------------------
const SPACE_AVATAR_PALETTE = [
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#A855F7',
  '#EC4899',
  '#3B82F6',
  '#14B8A6',
]

function spaceAvatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  return SPACE_AVATAR_PALETTE[
    Math.abs(hash) % SPACE_AVATAR_PALETTE.length
  ]
}

function SpaceAvatar({ name, color }) {
  return (
    <span
      className="inline-flex items-center justify-center h-5 w-5 rounded text-[10px] font-semibold text-white shrink-0"
      style={{ backgroundColor: color || spaceAvatarColor(name) }}
      aria-hidden="true"
    >
      {(name.trim().charAt(0) || 'S').toUpperCase()}
    </span>
  )
}

/** One Space row in the sidebar tree: an expand/collapse toggle (avatar +
 *  name), the hover `SpaceRowActions` (3-dot menu — rename / colour / share /
 *  add List / …), and — when expanded — the `SpaceChildren` sub-tree
 *  (Projects → Lists → Docs, each with their own row actions + active-List
 *  highlight). Shared by the Spaces section AND the Favourites section so a
 *  favourited Space behaves EXACTLY like it does under Spaces. Must be rendered
 *  inside a DndContext (SpaceChildren uses drag-and-drop). */
function SidebarSpaceNode({ space, workspaceId, expanded, onToggleExpand, onShare }) {
  return (
    <DroppableSpaceRow space={space}>
      <div className="group/space relative">
        <button
          type="button"
          aria-label={expanded ? `Collapse ${space.name}` : `Expand ${space.name}`}
          aria-expanded={expanded}
          onClick={onToggleExpand}
          className="w-full flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-sm text-left cursor-pointer transition-colors pr-14 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)]"
        >
          {expanded ? (
            <ChevronDown size={12} className="shrink-0 text-[color:var(--color-ink-subtle)]" />
          ) : (
            <ChevronRight size={12} className="shrink-0 text-[color:var(--color-ink-subtle)]" />
          )}
          <SpaceAvatar name={space.name} color={space.color ?? null} />
          <span className="truncate flex-1" title={space.name}>
            {space.name}
          </span>
        </button>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-100 lg:opacity-0 lg:group-hover/space:opacity-100 transition-opacity">
          <SpaceRowActions space={space} workspaceId={workspaceId} onShare={onShare} />
        </div>
      </div>
      {expanded ? (
        <div className="ml-4">
          <SpaceChildren spaceId={space.id} workspaceId={workspaceId} />
        </div>
      ) : null}
    </DroppableSpaceRow>
  )
}

/** Drop-target wrapper around a Space row + its children. The whole
 *  subtree counts as a drop zone so dragging onto either the header or
 *  any existing child means "move into this Space". */
function DroppableSpaceRow({ space, children }) {
  const data = { kind: 'space', spaceId: space.id }
  const { isOver, setNodeRef } = useDroppable({
    id: dropSpaceId(space.id),
    data,
  })
  return (
    <div
      ref={setNodeRef}
      className={
        'rounded-md transition-colors ' +
        (isOver
          ? 'bg-[color:var(--color-accent-bg)] outline outline-1 outline-[color:var(--color-accent-border)]'
          : '')
      }
    >
      {children}
    </div>
  )
}
